import { createTheme } from '@material-ui/core/styles';

// Type stacks shared with the marketing site (tcgscout-website), which loads the
// same pair via next/font and exposes them as --font-body / --font-display.
// Both faces are requested in public/index.html.
const bodyFont = ['Inter', 'system-ui', 'sans-serif'].join(',');
const displayFont = ['Sora', 'system-ui', 'sans-serif'].join(',');

// Raw theme options — NOT a resolved theme. `themeDark.js` imports this and
// passes it into `createTheme(themeOptions, darkOverrides)` so MUI merges both
// objects and runs createPalette/createTypography/augmentColor exactly once,
// against the final merged values. Never pass a resolved theme (the default
// export below) into another createTheme() call — see the note in themeDark.js.
export const themeOptions = {
  breakpoints: {
    values: {
      xxs: 320,
      xs: 400,
      sm: 768,
      md: 1048,
      lg: 1280,
      xl: 1430,
      xxl: 1920,
    },
  },
  typography: {
    // Body copy. Inter was already named here but never loaded, so every screen
    // was really rendering in the Helvetica Neue fallback. It is loaded from
    // index.html now.
    fontFamily: bodyFont,
    // Headings carry the display face, mirroring the marketing site's
    // --font-display / --font-body split. `themeDark.js` only overrides the
    // `color` of these seven variants; because both objects are merged by
    // createTheme before typography is resolved, fontFamily/fontWeight/fontSize
    // declared here flow through to dark mode without being redeclared there.
    h1: {
      fontFamily: displayFont,
      fontWeight: '800',
      color: '#22617B',
      fontSize: '24px',
    },
    h2: {
      fontFamily: displayFont,
      fontWeight: '800',
      color: '#22617B',
      fontSize: '18px',
    },
    h3: {
      fontFamily: displayFont,
      fontWeight: '800',
      color: '#22617B',
      fontSize: '16px',
    },
    h4: {
      fontFamily: displayFont,
      fontWeight: '600',
      color: '#828282',
      fontSize: '16px',
    },
    h5: {
      fontFamily: displayFont,
      fontWeight: '600',
      color: '#828282',
      fontSize: '14px',
    },
    h6: {
      fontFamily: displayFont,
      fontWeight: '700',
      color: '#666666',
      fontSize: '20px',
    },
    subtitle1: {
      fontFamily: displayFont,
      fontWeight: '800',
      color: '#2C9DC9',
      fontSize: '16px',
      // textDecoration: 'underline',
    },
    body1: {
      fontWeight: '500',
    },
    body2: {
      fontWeight: '400',
    },
  },
  sidebar: {
    drawerWidth: 267,
    drawerMobileWidth: '88%',
  },
  shadow: {
    main: '0px 5px 10px rgba(0, 0, 0, 0.05)',
  },
  palette: {
    primary: {
      main: '#1b5d77',
      white: '#FFFFFF',
      snow: '#F2F2F2',
      red: '#EB5757',
      blue: '#2196F3',
      gray: '#828282',
      lightGray: '#E0E0E0',
      middleGray: '#4F4F4F',
      darkGray: '#666666',
      success: '#52C851',
      error: '#F03738',
      warning: '#F3C41C',
      mainBlue: '#22617B',
    },
    secondary: {
      main: '#FFFFFF',
      gray: '#808B96',
      lightGray: '#EAEDED',
      blue: '#2C9DC9',
      lightBlue: '#D6EAF8',
      green: '#28B463',
      lightGreen: '#ABEBC6',
      orange: '#F2994A',
      lightOrange: '#FAEEEC',
      yellow: '#F1C40F',
      lightYellow: '#FCF3CF',
      purple: '#7D3C98',
      lightPurple: '#D2B4DE',
      bluegray: '#283747',
      lightBluegray: '#EBEDEF',
      red: '#E74C3C',
      lightRed: '#F5B7B1',
      darkRed: '#BC0000',
    },
    transparent: {
      main: '#1b5d7730',
      success: '#52C85130',
      warning: '#F3C41C30',
      error: '#F0373830',
    },
    // Semantic tokens. Every value here is the literal that was already
    // hardcoded in the components, so the light theme renders exactly as before.
    // `themeDark.js` overrides this same key set with its dark counterparts.
    custom: {
      // Surfaces
      pageBackground: '#FFFFFF',
      cardBackground: '#FFFFFF',
      cardAccentBackground: '#2C9DC910',
      hairline: '#E0E0E0',
      // Text
      heading: '#22617B',
      subheading: '#828282',
      strongText: '#1b5d77',
      mutedText: '#666666',
      accentText: '#2C9DC9',
      // Buttons
      softButtonBackground: '#2C9DC920',
      softButtonText: '#22617B',
      solidButtonBackground: '#2C9DC9',
      solidButtonHoverBackground: '#2eacdfff',
      // Inverted hover used by the "enable/disable" toggle buttons: pale blue
      // fill with the accent colour as the label.
      solidButtonHoverSurface: '#87CEFA',
      solidButtonText: '#FFFFFF',
      plainButtonBackground: '#FFFFFF',
      // Tooltips
      tooltipBackground: '#2196F3',
      // Brand-blue tooltip, used where the tooltip is meant to read as part of
      // the accent UI instead of the neutral `tooltipBackground` above.
      brandTooltipBackground: '#2C9DC9',
      tooltipText: '#FFFFFF',
      tooltipMutedText: '#666666',
      // Forms
      inputBackground: '#FFFFFF',
      inputLabel: '#808080',
      menuItemText: '#000000',
      // Icons / charts
      iconAccent: '#2C9DC9',
      iconMuted: '#4F4F4F',
      gaugeNeedle: '#1b5d77',
      // Folder affordance in the document browser. Kept identical in both themes
      // on purpose: it is a "this is a folder" signal borrowed from the OS file
      // manager, and the orange already contrasts well on either surface.
      folderIcon: '#ffa500',
      // Borders / surfaces
      imageBorder: '#ccc',
      // Backdrop behind third-party company logos (report cards). Deliberately
      // NOT inverted in dark mode: these are brand assets with transparent or
      // white backgrounds and dark ink, so they need a light surface to stay
      // legible no matter which theme is active.
      logoSurface: '#FFFFFF',
      // The wallet "credit card" graphic. Pinned to the same values in BOTH
      // themes: /nfc.png and /TCGHRES2021BACK.png are pure-white artwork on a
      // transparent background, so they are invisible on anything but a dark
      // fill. Lightening this card would erase the NFC icon and the logo.
      walletCardBackground: '#000000',
      walletCardText: '#FFFFFF',
      // The card's drop shadow DOES adapt: its job is to separate the card from
      // the page, and a light blue-grey smudge only reads as depth on white.
      walletCardShadow: '#BDC9D7',
      // Warning banners overlaid on the report card artwork. Opaque scrims, so
      // they read the same on either page surface.
      warningBannerBackground: 'rgba(255,221,80,0.95)',
      dangerBannerBackground: 'rgba(201, 76, 76, 0.95)',
      // High-contrast rules, darker than `hairline`: a solid separator bar and
      // the thick frame around the selected teleaudit image.
      strongDivider: '#000000',
      strongBorder: '#5c5c5cff',
      imagePlaceholderBackground: 'rgba(0, 0, 0, 0.1)',
      dropzoneBackground: '#fafafa',
      dropzoneBorder: '#aaa',
      // Brand-coloured button fill (deep teal). Separate from the overloaded
      // `primary.mainBlue`, which is a TEXT colour and goes light in dark mode.
      brandButtonBackground: '#22617B',
      // Accent handed to the Intercom widget, which renders outside React and
      // only accepts a colour string in its own settings. Mirrors `primary.main`
      // so the launcher matches the theme-toggle Fab beside it. Stored as hex on
      // purpose: Intercom expects hex, not the hsl() form the dark palette uses.
      intercomAccent: '#1b5d77',
      // Diff colouring in the file-import views
      diffAddedText: 'green',
      diffRemovedText: 'red',
      // Linear-progress fills. Kept apart from the status chip tokens because
      // these are saturated bars on a neutral track, not text on a pastel fill.
      progressBarInfo: '#1a90ff',
      progressBarDanger: '#E74C3C',
      progressBarWarning: '#F1C40F',
      progressBarSuccess: '#2ECC71',
      // Tables
      tableRowHover: 'rgb(44, 157, 201, 0.05)',
      tableHeaderBackground: '#f5f5f5',
      disabledInputText: 'rgba(0, 0, 0, 0.87)',
      // Disabled / error states
      disabledText: '#BDBDBD',
      disabledBackground: '#D3D3D3',
      // Slightly darker than `disabledBackground`; used as the fill of buttons
      // that are greyed out rather than of inert surfaces.
      disabledButtonBackground: '#cccccc',
      disabledIcon: '#EAEDED',
      dangerIcon: '#E74C3C',
      errorText: 'red',
      hoverSurface: '#EAEDED',
      // Status chips (ticket states, reused by other modules).
      // Light values come from the secondary.* pairs these replaced.
      statusOpenText: '#28B463',
      statusOpenBackground: '#ABEBC6',
      statusPendingText: '#F2994A',
      statusPendingBackground: '#FAEEEC',
      statusApprovedText: '#7D3C98',
      statusApprovedBackground: '#D2B4DE',
      statusNeutralText: '#808B96',
      statusNeutralBackground: '#EAEDED',
      statusClosedText: '#2C9DC9',
      statusClosedBackground: '#D6EAF8',
      statusRejectedText: '#E74C3C',
      statusRejectedBackground: '#F5B7B1',
      statusIdleText: '#283747',
      statusIdleBackground: '#EBEDEF',
      statusWarningText: '#F1C40F',
      statusWarningBackground: '#FCF3CF',
      // Inverted chip: saturated fill + white label. Used for "BLOQUEADO",
      // which is deliberately louder than the pastel chips above.
      statusBlockedText: '#FFFFFF',
      statusBlockedBackground: '#E74C3C',
      // Outlined / secondary button (light fill, colored label)
      outlineButtonBackground: '#FFFFFF',
      outlineButtonText: '#2C9DC9',
      // Green solid button (was `secondary.green` used as a background)
      successButtonBackground: '#28B463',
      successIcon: 'green',
      // Darker green solid button, used by the "Exportar Información" action.
      // Deliberately separate from the two greens above: it is ~13 points
      // darker, so folding it into either would be a visible light-mode change.
      exportButtonBackground: '#2e7d32',
      // Red solid button (destructive / cancel actions)
      dangerButtonBackground: '#E74C3C',
      // Bootstrap-family red, used for "remove" affordances rendered as plain
      // text/icon buttons rather than filled ones.
      dangerAccent: '#dc3545',
      // Action buttons in the video teleaudit toolbar. These were hardcoded with
      // Bootstrap's palette, which is a different family from the tokens above,
      // so they get their own keys rather than being folded into the near ones.
      infoButtonBackground: '#007bff',
      confirmButtonBackground: '#28a745',
      neutralButtonBackground: '#6c757d',
      processingButtonBackground: '#ff6b35',
      // Sidebar. It is a dark surface in both themes, so these are deliberately
      // separate from the page tokens above.
      sidebarBackground: '#22617B',
      sidebarText: '#FFFFFF',
      sidebarMutedText: '#808B96',
      // Mirrors what the expanded-menu icons actually render today: `iconOpen`
      // pointed at `secondary.middleGray`, which was never declared, so the SVGs
      // fall back to the ListItemIcon default (palette.action.active).
      // See the note in the migration summary if you want these to match
      // the collapsed state's gray instead.
      sidebarIcon: 'rgba(0, 0, 0, 0.54)',
      sidebarSelectedBackground: '#2C9DC9',
      sidebarSelectedText: '#FFFFFF',
      sidebarSelectorBackground: '#2C9DC9',
      sidebarSelectorText: '#FFFFFF',
      sidebarTooltipBackground: '#2C9DC9',
      sidebarTooltipText: '#FFFFFF',
      sidebarDividerClosed: '#4F4F4F',
      sidebarLogoutIcon: '#EB5757',
    },
    background: {
      default: 'hsl(210, 40%, 98%)',
    },
    text: {
      primary: 'hsl(0, 0%, 0%)',
      secondary: 'hsl(215, 20%, 65%)',
    },
  },
  overrides: {
    MuiButton: {
      root: {
        textTransform: 'none',
      },
      iconSizeMedium: {
        '& > *:first-child': {
          fontSize: '1.5rem',
        },
      },
      containedSecondary: {
        color: 'white',
      },
    },
    MuiAccordionSummary: {
      content: {
        margin: '12px 0',
        '&.Mui-expanded': {
          margin: '12px 0',
        },
      },
    },
    MuiSvgIcon: {
      root: {
        margin: '5px',
        fontSize: '1.5rem',
      },
    },
  },
};

const theme = createTheme(themeOptions);

export default theme;
